<template>
  <v-container fluid>

    <!-- Query -->
    <v-card dark :class="cardClass" :color="getCardColor()">

      <!-- Toolbar -->
      <v-toolbar dark dense flat :color="getToolbarColor()">

        <!-- Breed Chip -->
        <v-chip v-if="animalSourceHasMultipleBreeds(query)"
          small
          :class="query.active
            ? 'caption mr-2'
            : 'caption mr-2 grey--text text--lighten-1'"
          :color="query.active
            ? 'blue-grey lighten-2'
            : 'blue-grey lighten-1'"
            >
          {{ filterBreed }}
        </v-chip>

        <!-- Query Title -->
        <v-toolbar-title :class="titleClass">
          {{ query.title }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- Primary Trait Chip -->
        <v-chip v-if="(!isExtraSmallDevice && scope === 'bullfinder') || scope === 'herdtrax'"
          small
          :class="query.active
              ? 'caption'
              : 'caption grey--text text--lighten-1'"
          :color="query.active
            ? 'blue-grey lighten-2'
            : 'blue-grey lighten-1'"
            >
          {{ chipPrimaryTrait }}
        </v-chip>
       
        <!-- Animal Source Chip -->
        <!-- <v-chip v-if="scope === 'bullfinder'"
          small
          :class="query.active
            ? 'caption ml-2'
            : 'caption ml-2 grey--text text--lighten-1'"
          :color="query.active
            ? colourCode + ' darken-1'
            : colourCode + ' darken-4'"
            >
          {{ chipAnimalSourceCode }}
        </v-chip> -->
      </v-toolbar>

      <!-- Query Info -->
      <v-card-text class="py-3">
        <div :class="subtitle1Class"> {{ getQuerySubtitle1(query) }}</div>
        <div :class="subtitle2Class">{{ getQuerySubtitle2(query) }}</div>
      </v-card-text>
    </v-card>

    <!-- Query Detail Dialog -->
    <v-dialog v-model="detailDialog"
      :max-width="scope === 'herdtrax' ? '610px' : '570px'" 
      :persistent="true"
      >
      
      <!-- Header Toolbar -->
      <v-toolbar flat dark :color="colourCode + ' darken-4'">
        <v-btn icon :small="isSmallDevice" @click="cancel">
          <v-icon :small="isSmallDevice">close</v-icon>
        </v-btn>

        <v-toolbar-title :class="isSmallDevice ? 'body-2' : 'ml-1'">
          {{ activeQuery.title }}
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          :small="isSmallDevice"
          :loading="isSaving"
          :disabled="isSaving"
          @click="save"
          outlined
          >
          Save
          <span slot="loader" class="custom-loader">
            <v-icon>cached</v-icon>
          </span>
        </v-btn>
      </v-toolbar>

      <v-container
        :style="{ maxHeight: '690px', backgroundColor: 'white' }"
        class="overflow-y-auto pa-0 ma-0"
        >
        <user-query-def
          v-if="activeQuery !== null"
          v-bind:scope="scope"
          :optional-filters="optionalFilters"
          :traits-container="traitsContainer"
          :settings="detailSettings"
        />
      </v-container>
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import { AppColor, InputTypeId, CriterionTypeId, FilterName } from '@/config.js'
import UserQueryDef from '@/components/UserQueryDef'

export default {
  name: 'UserQuery',
  components: {
    UserQueryDef
  },
  props: {
    scope: {
      type: String,
      default: 'herdtrax',
    },
    query: {
      type: Object,
      default: null
    },
    activeQuery: {
      type: Object,
      default: null
    },
    userFilters: {
      type: Array,
      default: null
    },
    colourCode: {
      type: String,
      default: null
    },
    chipPrimaryTrait: {
      type: String,
      default: null
    },
    chipAnimalSourceCode: {
      type: String,
      default: null
    },
    isEditQueryClicked: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      appColor_ContentToolbar: AppColor.ContentToolbar,

      // input types
      inputTypeId_Dropdown: InputTypeId.Dropdown,
      inputTypeId_RangeSlider: InputTypeId.RangeSlider,
      inputTypeId_MultiSelect: InputTypeId.MultiSelect,

      // criterion types
      criterionTypeId_Filter: CriterionTypeId.Filter,
      criterionTypeId_Trait: CriterionTypeId.Trait,
      criterionTypeId_OptionalFilter: CriterionTypeId.Filter,

      // filter names
      filterName_BirthYear: FilterName.BirthYear,
      filterName_Breed: FilterName.Breed,

      optionalFilters: [],
      traitsContainer: [],
      isSaving: false,
      detailSettings: { isPrioritize: false, colourCode: '' },
      detailDialog: false,

      isEditClicked: this.isEditQueryClicked
    }
  },
  computed: {
    filterYear () {
      if (this.scope === 'herdtrax') return this.query.year

      const filter = this.filterName_BirthYear
      const yobFilters = this.userFilters.filter(f => { return f.propertyPath === filter })
      return yobFilters.length > 0
        ? yobFilters[0].displayValue
        : null 
    },
    filterBreed () {
      const filter = this.filterName_Breed
      const breedFilters = this.userFilters.filter(f => { return f.propertyPath === filter })
      return breedFilters.length > 0
        ? breedFilters[0].displayValue
        : null 
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name  === 'sm'
    },
    isExtraSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs'
    },

    // computed css classes
    cardClass () {
      return {
        'elevation-6': this.query.active,
        'elevation-0': !this.query.active
      }
    },
    titleClass () {
      const padding = 'pl-2'
      const fontWeight = this.isSmallDevice ? 'body-2' : ''
      return this.query.active 
        ? fontWeight + ' ' + padding
        : fontWeight + ' font-weight-light grey--text text--lighten-2 ' + padding
    },
    subtitle1Class () {
      return {
        'subtitle-1 grey--text': true,
        'text--lighten-5': this.query.active,
        'text--lighten-2': !this.query.active
      }
    },
    subtitle2Class () {
      return {
        'subtitle-2 grey--text': true,
        'text--lighten-4': this.query.active,
        'text--lighten-1': !this.query.active
      }
    }
  },
  methods: {
    save () {
      const self = this
      self.isSaving = true
      self.activeQuery.userQueryDef.optionalFilters = self.cloneOptionalFilters(self.optionalFilters)
      self.activeQuery.userQueryDef.userTraits = self.cloneUserTraits(self.traitsContainer.userTraits)
      const updateModel = self.getCriteriaForUpdate()
      appService.updateCriteria(self.activeQuery.id, updateModel, self.scope).then(() => {
        self.detailDialog = false
        this.$emit('change')
        setTimeout(() => {
          self.isSaving = false
        }, 300)
      })
    },

    getCriteriaForUpdate () {
      const self = this
      var criteriaModels = []
      const traits = self.activeQuery.userQueryDef.userTraits

      traits.forEach(t => {
        var values = t.multiSelectValues
        criteriaModels.push({
          id: t.traitId,
          criterionTypeId: self.criterionTypeId_Trait,
          inputTypeId: t.inputTypeId,
          value: t.inputTypeId === self.inputTypeId_MultiSelect
            ? values.join()
            : t.value,
          minMaxValue: t.minMaxValue !== null
            ? t.minMaxValue.map(String)
            : null
        })
      })

      if (self.scope === 'herdtrax') {
        const filters = self.activeQuery.userQueryDef.optionalFilters
        filters.forEach(f => {
          var values2 = f.multiSelectValues
          criteriaModels.push({
            id: f.filterId,
            criterionTypeId: self.criterionTypeId_OptionalFilter,
            inputTypeId: f.inputTypeId,
            value: f.inputTypeId === self.inputTypeId_MultiSelect
              ? values2.join()
              : f.value
          })
        })
      }

      return criteriaModels
    },

    cancel () {
      this.detailDialog = false
      this.$emit('cancel')
    },

    openDetail () {
      this.optionalFilters = this.cloneOptionalFilters(this.activeQuery.userQueryDef.optionalFilters)
      this.traitsContainer = { userTraits: this.cloneUserTraits(this.activeQuery.userQueryDef.userTraits) }
      this.detailSettings = { isPrioritize: false, colourCode: this.colourCode }
      this.detailDialog = true
    },

    cloneOptionalFilters (filters) {
      if (this.scope === 'bullfinder') return
      var result = []
      for (var key1 in filters) {
        var multiSelectItems = []
        var items = filters[key1].multiSelectItems
        for (var key2 in items) {
          multiSelectItems.push(items[key2])
        }
        var multiSelectValues = []
        items = filters[key1].multiSelectValues
        for (var key3 in items) {
          multiSelectValues.push(items[key3])
        }

        var dropdownItems = []
        items = filters[key1].dropdownItems
        for (var key4 in items) {
          dropdownItems.push({value: items[key4].value, text: items[key4].text})
        }
        result.push({
          id: filters[key1].id,
          filterId: filters[key1].filterId,
          inputTypeId: filters[key1].inputTypeId,
          dataTypeId: filters[key1].dataTypeId,
          value: filters[key1].value,
          displayValue: filters[key1].displayValue,
          description: filters[key1].description,
          dropdownItems: dropdownItems,
          multiSelectItems: multiSelectItems,
          multiSelectValues: multiSelectValues
        })
      }
      return result
    },

    cloneUserTraits (traits) {
      var result = []
      for (var key1 in traits) {
        var multiSelectItems = []
        var items = traits[key1].multiSelectItems
        for (var key2 in items) {
          multiSelectItems.push(items[key2])
        }
        var multiSelectValues = []
        items = traits[key1].multiSelectValues
        for (var key3 in items) {
          multiSelectValues.push(items[key3])
        }

        var dropdownItems = []
        items = traits[key1].dropdownItems
        for (var key4 in items) {
          dropdownItems.push({value: items[key4].value, text: items[key4].text})
        }
        result.push({
          id: traits[key1].id,
          group: traits[key1].group,
          traitId: traits[key1].traitId,
          description: traits[key1].description,
          inputTypeId: traits[key1].inputTypeId,
          dataTypeId: traits[key1].dataTypeId,
          searchTypeId: traits[key1].searchTypeId,
          value: traits[key1].value,
          minMaxRange: traits[key1].minMaxRange,
          minMaxValue: traits[key1].minMaxValue,
          minMaxDefault: traits[key1].minMaxDefault,
          dropdownItems: dropdownItems,
          multiSelectItems: multiSelectItems,
          multiSelectValues: multiSelectValues,
          increment: traits[key1].increment,
          unitId: traits[key1].unitId,
          unit: traits[key1].unit,
          unitAbbreviation: traits[key1].unitAbbreviation,
          sequence: traits[key1].sequence
        })
      }
      return result
    },

    getQuerySubtitle1 (query) {
      return this.scope === 'herdtrax'
        ? query.herd + ' (' + query.year + ')'
        : query.description !== null && query.description.length > 0
          ? 'YOB ' + this.filterYear + ' - ' + query.description
          : 'YOB ' + this.filterYear
    },

    getQuerySubtitle2 (query) {
      return this.scope === 'herdtrax'
        ? query.description
        : query.animalSource.name
    },

    animalSourceHasMultipleBreeds(query) {
      return this.scope !== 'herdtrax'
        ? query.animalSource.numberBreeds > 1
        : false
    },

    getToolbarColor () {
      return this.query.active
        ? this.colourCode + ' darken-4'
        : 'grey darken-2'
    },

    getCardColor () {
      return this.query.active
        ? this.colourCode + ' darken-2'
        : 'grey'
    }
  },
  watch: {
    isEditQueryClicked (value) {
      if (value === null) return
      this.isEditClicked = value
      if (value) {
        this.openDetail()
      }
    }
  }
}
</script>

